import type { DefaultMessagesKeys } from '@src/i18n/useTranslate';

/**
 * Translates a cart validation error code into an i18n key.
 *
 * The API answers a rejected cart item with 422 and a `type` per field (`tariffOccupationMustBe
 * Respected`, `tariffQuantityMustRespectProductLimit`, ...). Those codes reach the store untouched,
 * because the adder matches on them to decide where to send the shopper back to. They were also
 * what got rendered, so a rejected purchase showed the raw code — no indication of what went wrong.
 * Map the ones a buyer can act on, and fall back to a generic message for everything else.
 */
const CART_ERROR_MESSAGE_KEYS: Readonly<Record<string, DefaultMessagesKeys>> = {
  tariffQuantityMustRespectProductLimit: 'adder_error_tariff_quantity_limit',
  tariffOccupationMustBeRespected: 'adder_error_tariff_unavailable',
  tariffNotAllowedOnSchedule: 'adder_error_tariff_unavailable',
  thereAreDependentTariffsThatMustBeAccompanied: 'adder_dependent_tariff_selection_error',
  tariffVerificationValidationNotFulfilled: 'adder_tariff_verification_validation_error',
  additionalOccupationMustBeRespected: 'adder_error_additional_unavailable',
  saleAdditionalQuantityIsAvailable: 'adder_error_additional_unavailable',
  scheduleDoesNotExists: 'adder_error_schedule_unavailable',
  scheduleAntecedenceMustBeRespected: 'adder_error_schedule_unavailable',
  scheduleMustMatchProductSellingMode: 'adder_error_schedule_unavailable',
  resourceQuantityRespectsCapacity: 'adder_error_seats_unavailable',
  resourceQuantityRespectsCategoryUnderAllocation: 'adder_error_seats_unavailable',
  insufficientResourceCapacity: 'adder_error_seats_unavailable',
  categoryMinimumQuantityNotMet: 'adder_error_seats_unavailable',
  resourceMustBeAvailable: 'adder_error_seats_unavailable',
  productMustBeActive: 'adder_error_product_unavailable',
  productMustBeSellable: 'adder_error_product_unavailable',
};

export const GENERIC_CART_ERROR_MESSAGE_KEY: DefaultMessagesKeys = 'adder_error_generic';

export const cartErrorMessageKey = (error: string): DefaultMessagesKeys =>
  CART_ERROR_MESSAGE_KEYS[error] ?? GENERIC_CART_ERROR_MESSAGE_KEY;

/**
 * Collapses the per-field errors of one rejected request into the distinct messages to show. A
 * selection that busts the limit on four tariffs comes back as four identical codes; the shopper
 * needs to read the reason once.
 */
export const cartErrorMessageKeys = (errors: Record<string, string> | undefined): DefaultMessagesKeys[] => {
  if (!errors) return [];

  return [...new Set(Object.values(errors).map(cartErrorMessageKey))];
};
